import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/organization/devices")({
  component: RouteComponent,
});

function RouteComponent() {
  // Devices are attendance-tracking hardware, gated by the same capability
  // that governs the rest of attendance policy configuration.
  return (
    <ProtectedRoute requiredPermission="attendance.policy.manage">
      <DevicesDataTable />
    </ProtectedRoute>
  );
}

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { DeviceFormSheet } from "@/components/crud/forms/DeviceFormSheet";

// Hooks
import useFetchDevices, {
  type DeviceOrdering,
} from "@/hooks/api/organization/devices/useFetchDevices";
import useCreateDevice from "@/hooks/api/organization/devices/useCreateDevice";
import useUpdateDevice from "@/hooks/api/organization/devices/useUpdateDevice";
import useDeleteDevice from "@/hooks/api/organization/devices/useDeleteDevice";
import useSetDeviceActive from "@/hooks/api/organization/devices/useSetDeviceActive";

import { DeviceTable } from "@/components/crud/table/DeviceTable";
import { ConfirmDialog } from "@/components/crud/ConfirmDialog/ConfirmDialog";
import { DeviceServerLinkDialog } from "@/components/crud/modals/DeviceServerLinkDialog";
import useRotateDeviceToken from "@/hooks/api/organization/devices/useRotateDeviceToken";
import type { Devices, DevicePayload } from "@/types/organization/devices";
import PageHeader from "@/components/reusable-components/header/page-header";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";

function DevicesDataTable() {
  // UI State
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Devices | null>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<Devices | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
        serial_number: SortDirection;
        last_seen: SortDirection;
        last_sync: SortDirection;
        last_updated: SortDirection;
      },
      DeviceOrdering
    >({
      name: null,
      created_at: null,
      updated_at: null,
      serial_number: null,
      last_seen: null,
      last_sync: null,
      last_updated: null,
    });

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Device Crud Hooks
  const devicesQuery = useFetchDevices({
    page_size: pageSize,
    page,
    ordering: getApiOrdering(),
  });
  const devices = devicesQuery.data?.results ?? [];
  const count = devicesQuery.data?.count ?? 0;

  const createDevice = useCreateDevice();
  const updateDevice = useUpdateDevice();
  const deleteDevice = useDeleteDevice();
  const setDeviceActive = useSetDeviceActive();

  const isMutating =
    createDevice.isPending ||
    updateDevice.isPending ||
    deleteDevice.isPending ||
    setDeviceActive.isPending;
  const isLoading = devicesQuery.isPending || isMutating;
  const isFetching = devicesQuery.isFetching || isMutating;

  // When ordering changes, go back to page 1
  useEffect(() => {
    setPage(1);
  }, [ordering, setPage]);

  // Handlers
  const handleCreate = () => {
    setEditingDevice(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (device: Devices) => {
    setEditingDevice(device);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (device: Devices) => {
    setEditingDevice(device);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const rotateDeviceToken = useRotateDeviceToken();
  const [deviceToRotate, setDeviceToRotate] = useState<Devices | null>(null);
  const [rotatedDevice, setRotatedDevice] = useState<Devices | null>(null);

  const handleRotateConfirm = async () => {
    if (!deviceToRotate) return;
    try {
      const updated = await rotateDeviceToken.mutateAsync(deviceToRotate.id);
      setDeviceToRotate(null);
      setRotatedDevice(updated);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleDeleteClick = (device: Devices) => {
    setDeviceToDelete(device);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deviceToDelete) return;
    try {
      await deleteDevice.mutateAsync({ id: deviceToDelete.id });
      toast.success("Device deactivated successfully!");
      setIsDeleteDialogOpen(false);
      setDeviceToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleActivate = async (device: Devices) => {
    try {
      await setDeviceActive.mutateAsync({ id: device.id, isActive: true });
      toast.success("Device activated successfully!");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: DevicePayload) => {
    if (editingDevice) {
      await updateDevice.mutateAsync({ id: editingDevice.id, payload: data });
      toast.success("Device updated successfully!");
    } else {
      await createDevice.mutateAsync(data);
      toast.success("Device registered successfully!");
    }

    setIsSheetOpen(false);
  };

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Devices"
          description="Manage organization attendance devices here."
          onAdd={handleCreate}
          addLabel="Register Device"
          isLoading={isFetching}
        />

        {/* Devices Table */}
        <DeviceTable
          devices={devices}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onActivate={handleActivate}
          onRotateToken={setDeviceToRotate}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {/* Device Form Sheet */}
        <DeviceFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingDevice={editingDevice}
          isViewOnly={isViewOnly}
          isLoading={isMutating}
        />

        {/* Rotate server link (T-101) */}
        <ConfirmDialog
          isOpen={!!deviceToRotate}
          onClose={() => setDeviceToRotate(null)}
          onConfirm={handleRotateConfirm}
          title="Rotate device link"
          description={`"${deviceToRotate?.name ?? ""}" stops syncing until its server URL is updated with the new link. Punches wait on the device and upload once it reconnects.`}
          confirmLabel="Rotate link"
          loadingLabel="Rotating..."
          isLoading={rotateDeviceToken.isPending}
          variant="destructive"
        />
        <DeviceServerLinkDialog
          device={rotatedDevice}
          onClose={() => setRotatedDevice(null)}
        />

        {/* Delete Confirmation */}
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Deactivate Device"
          description={`"${deviceToDelete?.name}" will stop syncing with TOMS. It stays in this list as Inactive and its attendance history is kept.`}
          itemName={deviceToDelete?.name}
          isLoading={deleteDevice.isPending}
          confirmLabel="Deactivate"
          loadingLabel="Deactivating..."
          destructive
        />
      </div>
    </SidebarInset>
  );
}
